import type { SxProps } from '@mui/material';
import {
  type DataTableTanstackTable,
  dataTableMetaToCellProps,
  getDataTableColumnMeta,
} from '@ovComponents/2-component/dataTable';
import {
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import type { Header } from '@tanstack/react-table';
import type { MouseEventHandler, ReactNode } from 'react';

export type PortfolioTradingTableHeadLeadingCell = {
  key: string;
  content?: ReactNode;
  sx?: SxProps;
};

type PortfolioTradingTableHeadProps<T> = {
  table: DataTableTanstackTable<T>;
  skipColumnIds?: ReadonlySet<string>;
  leadingCells?: PortfolioTradingTableHeadLeadingCell[];
  trailingCells?: PortfolioTradingTableHeadLeadingCell[];
  rowSx?: SxProps;
  /** Merged onto data column header cells (e.g. portfolios table section/sort styling). */
  dataHeaderCellSx?: SxProps;
};

function asSxArray(sx: SxProps | undefined): SxProps[] {
  if (sx == null) return [];
  return Array.isArray(sx) ? [...sx] : [sx];
}

/**
 * Only clip when a fixed width was set (e.g. portfolioColumnMeta) —
 * gives `headerLabelSx` ellipsis a boundary without affecting the
 * other PortfolioTradingTableHead consumers that size columns freely.
 */
function getDataHeaderCellSx(
  metaSx: SxProps | undefined,
  dataHeaderCellSx: SxProps | undefined,
): SxProps {
  return [
    ...asSxArray(metaSx),
    ...asSxArray(dataHeaderCellSx),
    { overflow: 'hidden' },
  ] as SxProps;
}

function getHeaderSortClickHandler<T>(
  header: Header<T, unknown>,
  canSort: boolean,
): MouseEventHandler<HTMLTableCellElement> | undefined {
  return canSort ? header.column.getToggleSortingHandler() : undefined;
}

/** String headers only; placeholders and render-fn headers render empty. */
function getHeaderLabel<T>(header: Header<T, unknown>): string | null {
  if (header.isPlaceholder) return null;
  const { header: columnHeader } = header.column.columnDef;
  return typeof columnHeader === 'string' ? columnHeader : null;
}

export function PortfolioTradingTableHead<T>({
  table,
  skipColumnIds = new Set(),
  leadingCells = [],
  trailingCells = [],
  rowSx,
  dataHeaderCellSx,
}: PortfolioTradingTableHeadProps<T>) {
  const headers =
    table
      .getHeaderGroups()[0]
      ?.headers.filter((header) => !skipColumnIds.has(header.column.id)) ?? [];

  return (
    <TableHead>
      <TableRow sx={rowSx}>
        {leadingCells.map(({ key, content, sx }) => (
          <TableHeadCell key={key} headerBare sx={sx}>
            {content}
          </TableHeadCell>
        ))}
        {headers.map((header) => {
          const meta = getDataTableColumnMeta(header.column);
          const sortDisabled = Boolean(meta?.disableDataTableSortHandling);
          const canSort = header.column.getCanSort() && !sortDisabled;
          const sorted = header.column.getIsSorted();
          const { sx: metaSx, ...cellPropsFromMeta } =
            dataTableMetaToCellProps(meta);

          return (
            <TableHeadCell
              key={header.id}
              colSpan={header.colSpan}
              {...cellPropsFromMeta}
              sx={getDataHeaderCellSx(metaSx, dataHeaderCellSx)}
              headLabelVariant={meta?.headerLabelVariant}
              headLabelWeight={meta?.headerLabelWeight}
              headLabelSx={meta?.headerLabelSx}
              textColor={meta?.headerTextColor}
              headerBare={meta?.headerBare}
              isSortable={canSort}
              sortDirection={
                sorted === false ? undefined : (sorted as 'asc' | 'desc')
              }
              onClick={getHeaderSortClickHandler(header, canSort)}
            >
              {getHeaderLabel(header)}
            </TableHeadCell>
          );
        })}
        {trailingCells.map(({ key, content, sx }) => (
          <TableHeadCell key={key} headerBare sx={sx}>
            {content}
          </TableHeadCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export const PORTFOLIO_TRADING_PHANTOM_COLUMN_IDS = {
  portfolios: new Set(['select']),
  ticker: new Set(['select', 'expand']),
  model: new Set(['select', 'expand']),
} as const;
